import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Alert, Box, Button, Divider, Drawer, Stack, Typography } from '@mui/material';
import { describeBasis, type MetricBasis } from '@/lib/metricBasis';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * SZUFLADA DOWODU — jeden kanoniczny komponent dla całego Control Center
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Po co: analityka bez warstwy dowodu zmusza do wiary. Z nią — pozwala sprawdzić. Właściciel,
 * który zobaczy liczbę i nie może dojść, skąd się wzięła, prędzej czy później przestanie jej
 * ufać całej — łącznie z tymi liczbami, które są poprawne.
 *
 * Dlaczego JEDEN komponent, a nie sekcja „skąd to" na każdym ekranie: dowód jest własnością
 * LICZBY, a nie widoku, który akurat ją pokazuje. Ta sama wartość otwarta z pulpitu i z ekranu
 * sprzedaży musi pokazać to samo wyprowadzenie. Dwa niezależnie napisane opisy tej samej rzeczy
 * rozjeżdżają się przy pierwszej zmianie definicji — i wtedy produkt kłamie w jednym z dwóch
 * miejsc, nie wiadomo w którym.
 *
 * ┌──────────────────────────────────────────────────────────────────────────────────────────┐
 * │ POLE `czegoNieWynika` JEST OBOWIĄZKOWE.                                                   │
 * │ Nie jest to ozdoba ani asekuracja. To najczęstsze miejsce, w którym produkty analityczne  │
 * │ wprowadzają w błąd: pokazują poprawną liczbę i milczą o tym, czego z niej NIE widać.      │
 * │ „Wartość wg cennika" przeczytana jako przychód to błąd rzędu kilkunastu procent, podjęty  │
 * │ w dobrej wierze na poprawnej liczbie. Typ wymusza odpowiedź.                              │
 * └──────────────────────────────────────────────────────────────────────────────────────────┘
 */

/** Skąd pochodzą dane i czym one są — nie mylić świeżości z brakiem zdarzeń. */
export type KlasyfikacjaZrodla =
  /** Rzeczywiste dane środowiska TEST. */
  | 'REALNE_TEST'
  /** Zbiór kontrolowany: prawdziwy schemat i API, dane wprowadzone na potrzeby weryfikacji. */
  | 'TEST_KONTROLOWANE'
  /**
   * Realne dane REFERENCYJNE środowiska TEST: oferta, słownik, karta — nie sprzedaż.
   * Wolno użyć tylko wtedy, gdy proweniencja I data źródła są udowodnione. Rozdzielone od
   * `REALNE_TEST`, bo „realne" bez tego rozróżnienia bywa czytane jako „realna sprzedaż".
   */
  | 'TEST_REALNE_REFERENCYJNE'
  /** Dane produkcyjne. */
  | 'PRODUKCJA'
  /**
   * Dane produkcyjne, ale zbiór jest RZADKI — historii jest tyle, ile jej naprawdę jest.
   *
   * Osobna klasa, bo „dane produkcyjne" nad czterema słupkami czyta się jako awarię wykresu,
   * a nie jako stan firmy. Drive na produkcji ma 54 dostawy z trzech tygodni (zmierzone
   * 2026-08-21) i to jest CAŁY zbiór — nie skutek zawężenia zakresu. Zamazanie tej różnicy
   * kończy się albo szukaniem nieistniejącego błędu, albo dopisywaniem danych, żeby „wyglądało".
   */
  | 'PRODUKCJA_RZADKIE_DANE'
  /** Źródło istnieje, ale nie wiadomo, z którego momentu pochodzi jego zawartość. */
  | 'SWIEZOSC_NIEUSTALONA'
  /** Źródła nie ma — nie ma czego klasyfikować. */
  | 'BRAK_ZRODLA';

export const OPIS_KLASYFIKACJI: Record<KlasyfikacjaZrodla, string> = {
  REALNE_TEST: 'realne dane środowiska TEST',
  TEST_KONTROLOWANE: 'zbiór kontrolowany (realne API i schemat, dane wprowadzone do weryfikacji)',
  TEST_REALNE_REFERENCYJNE: 'realne dane referencyjne środowiska TEST (oferta i słowniki, NIE sprzedaż)',
  PRODUKCJA: 'dane produkcyjne',
  PRODUKCJA_RZADKIE_DANE: 'dane produkcyjne — zbiór rzadki, to cała dostępna historia',
  SWIEZOSC_NIEUSTALONA: 'świeżość źródła NIEUSTALONA',
  BRAK_ZRODLA: 'źródło nie istnieje',
};

export interface Dowod {
  /** Nazwa liczby, nie nazwa ekranu. */
  tytul: string;
  /** Sformatowana wartość — dokładnie ta, którą widać na ekranie. */
  wartosc?: string | null;
  etykietaWartosci?: string;

  /** Kontrakt metryk (ADR-001/002/003): podstawa kwoty, jednostka wolumenu, oś czasu. */
  podstawa?: MetricBasis;

  zrodlo: string;
  klasyfikacja: KlasyfikacjaZrodla;
  /** Data odczytu albo jawne „nieustalona". Nigdy zmyślony stempel. */
  swiezosc: string;

  oknoBiezace?: string;
  oknoPorownawcze?: string | null;

  /** Wzór lub słowny opis wyliczenia. */
  wyliczenie: string;
  /** Pary klucz–wartość: statusy, filtry, progi. */
  szczegoly?: ReadonlyArray<readonly [string, string]>;

  /** Wielkość próby i — jeśli istnieje — wymagane minimum. */
  proba?: string;
  wymaganeMinimum?: string;
  jakoscDanych?: string;

  /** Ograniczenia wyniku: braki, pokrycie, zakres. */
  ograniczenia?: readonly string[];

  /** OBOWIĄZKOWE. Czego z tej liczby NIE wolno wywnioskować. */
  czegoNieWynika: string;
}

type Kontekst = { pokaz: (d: Dowod) => void };

const KontekstDowodu = createContext<Kontekst | null>(null);

/**
 * Hak do otwierania dowodu z dowolnego miejsca. Świadomie NIE rzuca wyjątku poza providerem —
 * brak dowodu nie może wywrócić ekranu z danymi. W testach jednostkowych renderujących pojedynczy
 * komponent bez powłoki dowód po prostu nie działa, i to jest w porządku.
 */
export function useDowod(): Kontekst {
  return useContext(KontekstDowodu) ?? { pokaz: () => {} };
}

function Wiersz({ etykieta, wartosc }: { etykieta: string; wartosc: ReactNode }) {
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        py: 1.1,
        borderBottom: '1px dashed var(--linia)',
      }}
    >
      <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)', flex: 'none' }}>
        {etykieta}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }} className="sygnal-liczba">
        {wartosc}
      </Typography>
    </Stack>
  );
}

function Blok({ tytul, children }: { tytul: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)', display: 'block', mb: 0.5 }}>
        {tytul}
      </Typography>
      {children}
    </Box>
  );
}

export function DostawcaDowodu({ children }: { children: ReactNode }) {
  const [dowod, setDowod] = useState<Dowod | null>(null);
  const pokaz = useCallback((d: Dowod) => setDowod(d), []);
  const wartosc = useMemo(() => ({ pokaz }), [pokaz]);

  return (
    <KontekstDowodu.Provider value={wartosc}>
      {children}
      <Drawer
        anchor="right"
        open={dowod !== null}
        onClose={() => setDowod(null)}
        slotProps={{
          paper: {
            'aria-label': 'Dowód',
            sx: { width: { xs: '100vw', sm: 520 }, p: 0, background: 'var(--powierzchnia)' },
          },
        }}
      >
        {dowod ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Stack
              direction="row"
              spacing={2}
              sx={{ p: 3, pb: 2, borderBottom: '1px solid var(--linia)', alignItems: 'flex-start' }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
                  Dowód
                </Typography>
                <Typography variant="h6">{dowod.tytul}</Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              <Button size="small" onClick={() => setDowod(null)} aria-label="Zamknij dowód">
                Zamknij
              </Button>
            </Stack>

            <Stack spacing={3} sx={{ p: 3, overflowY: 'auto', flexGrow: 1 }}>
              {dowod.wartosc !== undefined ? (
                <Box sx={{ p: 2, borderRadius: 'var(--r-maly)', background: 'var(--powierzchnia-2)' }}>
                  <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)', display: 'block' }}>
                    {dowod.etykietaWartosci ?? 'Wynik'}
                  </Typography>
                  <Typography variant="h5" className="sygnal-liczba">
                    {dowod.wartosc ?? 'brak danych'}
                  </Typography>
                  {dowod.podstawa ? (
                    <Typography variant="caption" sx={{ color: 'var(--tekst-2)', display: 'block', mt: 0.5 }}>
                      {describeBasis(dowod.podstawa)}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}

              <Blok tytul="Jak to policzono">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {dowod.wyliczenie}
                </Typography>
                {dowod.oknoBiezace ? <Wiersz etykieta="Okno bieżące" wartosc={dowod.oknoBiezace} /> : null}
                {dowod.oknoPorownawcze !== undefined ? (
                  <Wiersz etykieta="Okno porównawcze" wartosc={dowod.oknoPorownawcze ?? 'brak'} />
                ) : null}
                {(dowod.szczegoly ?? []).map(([k, v]) => (
                  <Wiersz key={k} etykieta={k} wartosc={v} />
                ))}
              </Blok>

              <Blok tytul="Źródło">
                <Wiersz etykieta="System" wartosc={dowod.zrodlo} />
                <Wiersz etykieta="Klasyfikacja" wartosc={OPIS_KLASYFIKACJI[dowod.klasyfikacja]} />
                <Wiersz etykieta="Świeżość" wartosc={dowod.swiezosc} />
              </Blok>

              {dowod.proba || dowod.jakoscDanych ? (
                <Blok tytul="Siła próby">
                  {dowod.proba ? <Wiersz etykieta="Próba" wartosc={dowod.proba} /> : null}
                  {dowod.wymaganeMinimum ? (
                    <Wiersz etykieta="Wymagane minimum" wartosc={dowod.wymaganeMinimum} />
                  ) : null}
                  {dowod.jakoscDanych ? <Wiersz etykieta="Jakość danych" wartosc={dowod.jakoscDanych} /> : null}
                </Blok>
              ) : null}

              {dowod.ograniczenia?.length ? (
                <Blok tytul="Ograniczenia">
                  <Stack component="ul" sx={{ m: 0, pl: 2.5 }} spacing={0.5}>
                    {dowod.ograniczenia.map((o) => (
                      <Typography key={o} component="li" variant="body2" sx={{ color: 'var(--tekst-2)' }}>
                        {o}
                      </Typography>
                    ))}
                  </Stack>
                </Blok>
              ) : null}

              <Divider />

              {/* Najważniejszy blok w całej szufladzie — i dlatego ostatni, czyli ten, na którym
                  zatrzymuje się wzrok, oraz jedyny w kolorze ostrzeżenia. */}
              <Alert severity="warning" icon={false} sx={{ borderRadius: 'var(--r-maly)' }}>
                <Typography variant="overline" sx={{ display: 'block', mb: 0.5 }}>
                  Czego z tej liczby NIE wynika
                </Typography>
                <Typography variant="body2">{dowod.czegoNieWynika}</Typography>
              </Alert>
            </Stack>
          </Box>
        ) : null}
      </Drawer>
    </KontekstDowodu.Provider>
  );
}

/** Przycisk otwierający dowód. Zawsze przy liczbie, nigdy w przypisie na dole ekranu. */
export function PrzyciskDowodu({
  dowod,
  etykieta = 'Skąd ta liczba',
  kartaId,
}: {
  dowod: Dowod;
  etykieta?: string;
  /**
   * Stabilny identyfikator karty dla walidacji DOM (R5-15) — ta sama konwencja co w
   * `PlytaGlowna`, `MiniMetryka`, `MetricCard` i `Karta`.
   *
   * Ten komponent jest współdzielony przez kilkanaście ekranów, a jego domyślna etykieta
   * („Skąd ta liczba") jest identyczna wszędzie — czyli dopasowanie po tekście wskazuje
   * na wszystkie wystąpienia naraz i nie rozstrzyga żadnego. Identyfikator MUSI więc
   * pochodzić z miejsca użycia, nie z komponentu.
   *
   * Przycisk nie ma własnego opakowania, więc atrybut siada na samym `<Button>`.
   * Nie zmienia wyglądu ani zachowania — jest wyłącznie adresem.
   */
  kartaId?: string;
}) {
  const { pokaz } = useDowod();
  return (
    <Button
      size="small"
      onClick={() => pokaz(dowod)}
      sx={{ px: 1.25, minWidth: 0 }}
      data-cc-card-id={kartaId}
    >
      {etykieta}
    </Button>
  );
}
